import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import { IconModule } from '@freelancer/ui/icon';
import { IconColor, IconName, IconSize } from '@freelancer/ui/icon';
import { SpinnerComponent } from '@freelancer/ui/spinner';
import { SpinnerColor, SpinnerSize } from '@freelancer/ui/spinner';
import {
  FontColor,
  FontWeight,
  TextSize,
  TextTransform,
} from '@freelancer/ui/text';
import { textVariants } from '../tailwind-text';
import {
  FloatingActionColor,
  FloatingActionPosition,
  FloatingActionSize,
} from './floating-action.types';

@Component({
  standalone: true,
  selector: 'fl-floating-action',
  imports: [IconModule, SpinnerComponent],
  template: `
    <div
      class="FloatingAction"
      [class.FloatingAction--withIcon]="icon"
      [attr.data-color]="color"
      [attr.disabled]="busy"
    >
      <div class="FloatingAction-inner">
        @if (icon) {
          <div class="FloatingAction-icon mr-xxxsmall">
            <fl-icon
              [name]="icon"
              [color]="iconColor"
              [size]="iconSize"
              [useIconFont]="useIconFont"
            ></fl-icon>
          </div>
        }
        <p [class]="fabTextClass">
          {{ text }}
        </p>
      </div>

      @if (busy) {
        <fl-spinner
          class="FloatingAction-spinner"
          [size]="SpinnerSize.SMALL"
          [color]="SpinnerColor.LIGHT"
        ></fl-spinner>
      }
    </div>
  `,
  styleUrls: ['./floating-action.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FloatingActionComponent implements OnInit, OnChanges {
  SpinnerColor = SpinnerColor;
  SpinnerSize = SpinnerSize;

  @Input() color: FloatingActionColor = FloatingActionColor.PRIMARY;
  @Input() icon: IconName;
  @Input() useIconFont = false;

  @HostBinding('attr.data-position')
  @Input()
  position: FloatingActionPosition = FloatingActionPosition.BOTTOM;

  @Input() size: FloatingActionSize = FloatingActionSize.SMALL;
  @Input() text: string;

  @HostBinding('attr.data-busy')
  @Input()
  busy?: boolean;

  @HostBinding('attr.role') role = 'button';
  @HostBinding('attr.aria-live') ariaLive = 'assertive';

  iconSize = IconSize.SMALL;
  iconColor = IconColor.LIGHT;
  textSize = TextSize.XXXSMALL;
  textColor = FontColor.LIGHT;

  fabTextClass: string;

  ngOnInit(): void {
    this.calculateStyles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('size' in changes || 'color' in changes) {
      this.calculateStyles();
    }
  }

  private calculateStyles(): void {
    switch (this.size) {
      case FloatingActionSize.MID:
        this.iconSize = IconSize.MID;
        this.textSize = TextSize.BODY_16;
        break;
      default:
        this.iconSize = IconSize.SMALL;
        this.textSize = TextSize.XXXSMALL;
        break;
    }

    switch (this.color) {
      case FloatingActionColor.DEFAULT:
        this.iconColor = IconColor.DARK;
        this.textColor = FontColor.DARK;
        break;
      default:
        this.iconColor = IconColor.LIGHT;
        this.textColor = FontColor.LIGHT;
        break;
    }

    this.fabTextClass = textVariants({
      color: this.textColor,
      size: this.textSize,
      weight: FontWeight.BOLD,
      transform: TextTransform.UPPERCASE,
      class: 'whitespace-nowrap',
    });
  }
}
